import { formatInTimeZone } from 'date-fns-tz';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@components/uikit/Button/Button';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import ModalHeader from '../ModalHeader/ModalHeader';
import ModalSteps from '../ModalSteps/ModalSteps';
import { ENVIRONMENT, type TEnvironment } from './constants/environmentsOptions';
import { type IWarrantyForm, getWarrantyDefaultValues } from './constants/schemaWarranty';
import { getStepName } from './helpers/getStepName';
import { getWarrantySteps } from './helpers/getWarrantySteps';
import { useClaimFormMutation } from './services/useClaimFormMutation';
import style from './style.module.scss';

const WarrantyModal = () => {
  const { t } = useTranslation();

  const { setModalClose } = useModalActions();

  const [step, setStep] = useState<number | string>(0);

  const { data: profileData } = useUserProfileQuery();

  const stepsList = getWarrantySteps();
  const currentStep = stepsList[step as number];

  const CurrentStepComponent = currentStep?.stepComponent;

  const progressBarSteps = stepsList.slice(0, stepsList.length);

  const { control, handleSubmit, reset, trigger } = useForm<IWarrantyForm>({
    mode: 'onChange',
    resolver: currentStep?.schema,
    defaultValues: getWarrantyDefaultValues(profileData?.user),
  });

  const handleClose = () => {
    setModalClose();
    reset();
  };

  const stepName = getStepName(t, currentStep.step);

  const { mutate: claimFormMutate, isLoading: claimFormLoading } = useClaimFormMutation();

  const submit = handleSubmit((data) => {
    const whatEnvironment = Object.keys(data.whatEnvironment).filter(
      (key) => data.whatEnvironment[key as TEnvironment] && key !== ENVIRONMENT.OTHER,
    );

    const formData = {
      fullName: data.fullName,
      title: data.title,
      email: data.email,
      phone: data.phone,
      companyFacility: data.companyFacility,
      shippingAddress: data.shippingAddress,
      productType: data.productType,
      deviceNumber: data.deviceNumber,
      equipmentLocation: data.equipmentLocation,
      installDate: data.installDate
        ? formatInTimeZone(new Date(data.installDate), 'UTC', 'yyyy/MM/dd HH:mm')
        : null,
      whatEnvironment: data.other ? [...whatEnvironment, data.other] : whatEnvironment,
      description: data.description,
      attachments: data.attachments,
    };

    claimFormMutate(formData, {
      onSuccess: () => {
        showToast({ variant: 'success', message: t('Warranty form has been sent.') });
        handleClose();
      },
      onError: (error) => {
        if (error) {
          showToast({
            variant: 'error',
            message: error?.response?.data?.message || t('Something went wrong! Please, try again later.'),
          });
        }
      },
    });
  });

  const handleNextStep = async () => {
    const nextStep = currentStep.nextStepIndex;

    if (nextStep === 'end') {
      submit();
      return;
    }

    const isValid = await trigger();

    if (isValid) {
      setStep(nextStep);
    }
  };

  const handleBack = () => {
    const previousStep = currentStep.previousStepIndex;

    if (previousStep === 'close') {
      handleClose();
      return;
    }

    setStep(previousStep);
  };

  return (
    <div className={style.warrantyModal}>
      <ModalHeader title={'Waites Warranty Claim Form'} variant={'withoutSeparator'} onClose={handleClose} />
      <ModalSteps stepName={stepName} currentStep={currentStep?.step} steps={progressBarSteps} />
      <div className={style.modalBody}>
        <CurrentStepComponent control={control} />
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Back')} onClick={handleBack} />
        <Button
          type="primary"
          variant={'medium'}
          text={t(currentStep?.nextStepIndex === 'end' ? 'Submit' : 'Next')}
          disabled={claimFormLoading}
          onClick={handleNextStep}
        />
      </div>
    </div>
  );
};

export default WarrantyModal;
